/**
 * Identificativi dell'anagrafe comune — funzioni PURE, condivise fra console, gestionale,
 * app e (via sync) Cloud Functions. Nessun import di Firebase, di React o di i18n.
 *
 * Tre modi di riconoscere un componente, in ordine di affidabilità (capitolo 162):
 *   1. il codice a barre standard GS1 (GTIN-8/12/13/14), quando c'è: attributo, MAI chiave;
 *   2. il marchio + il codice del costruttore scritto in un modo solo (`mpn_normalizzato`);
 *   3. la mappa degli identificativi (codici fornitore, descrizioni ricorrenti in fattura).
 *
 * ⚠️ Nel fotovoltaico il GTIN spesso NON esiste sulle etichette: le funzioni qui sotto
 * servono a VALIDARE ciò che arriva, non a inventarlo.
 */

/** Il codice del costruttore scritto in un modo solo: maiuscolo, solo lettere e cifre. */
export function normalizzaMpn(codice: string | null | undefined): string | null {
  const s = String(codice ?? '').toUpperCase().replace(/[^A-Z0-9]+/g, '');
  return s === '' ? null : s;
}

/** La forma con cui due scritture sono «lo stesso marchio»: maiuscolo, senza accenti, solo lettere e cifre. */
export function normalizzaNomeMarchio(nome: string | null | undefined): string {
  return String(nome ?? '')
    .toUpperCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Z0-9]+/g, '');
}

/** Tiene solo le cifre: «8 012345 678905» → «8012345678905». */
export function normalizzaGtin(valore: string | null | undefined): string {
  return String(valore ?? '').replace(/\D+/g, '');
}

export type EsitoGtin =
  | { valido: true; gtin: string; lunghezza: 8 | 12 | 13 | 14 }
  | { valido: false; motivo: 'vuoto' | 'lunghezza' | 'cifra_di_controllo'; gtin: string };

/**
 * Verifica un GTIN secondo la cifra di controllo GS1 (modulo 10, pesi 3 e 1 da destra).
 * Accetta GTIN-8, GTIN-12 (UPC-A), GTIN-13 (EAN-13) e GTIN-14. Il valore torna già pulito.
 */
export function validaGtin(valore: string | null | undefined): EsitoGtin {
  const gtin = normalizzaGtin(valore);
  if (gtin === '') return { valido: false, motivo: 'vuoto', gtin };
  const lunghezza = gtin.length;
  if (lunghezza !== 8 && lunghezza !== 12 && lunghezza !== 13 && lunghezza !== 14) {
    return { valido: false, motivo: 'lunghezza', gtin };
  }
  const cifre = gtin.split('').map((c) => Number(c));
  const controllo = cifre[cifre.length - 1];
  let somma = 0;
  // Pesi 3,1,3,1… partendo dalla cifra immediatamente a sinistra di quella di controllo.
  for (let i = cifre.length - 2, peso = 3; i >= 0; i -= 1, peso = peso === 3 ? 1 : 3) somma += cifre[i] * peso;
  const atteso = (10 - (somma % 10)) % 10;
  if (atteso !== controllo) return { valido: false, motivo: 'cifra_di_controllo', gtin };
  return { valido: true, gtin, lunghezza };
}

/** Unità con cui si conta un componente sullo scaffale. Id ASCII, mai tradotti (etichette a parte). */
export const UNITA_MISURA_BASE = ['pz', 'm', 'mq', 'mc', 'kg', 'l'] as const;
export type UnitaMisuraBase = (typeof UNITA_MISURA_BASE)[number];

export function isUnitaMisuraBase(v: unknown): v is UnitaMisuraBase {
  return typeof v === 'string' && (UNITA_MISURA_BASE as readonly string[]).includes(v);
}
